/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import { whitelist } from '@openphone/lib/collections'

import config from '@src/config'
import type {
  CodableCsvImportV2,
  CsvImportStatus,
  CsvImportV2MappingValue,
} from '@src/service/model/CsvImportV2Model'
import type { RawContactItem } from '@src/service/model/contact/ContactItemModel'
import type { CodableContact } from '@src/service/model/contact/ContactModel'
import type {
  ContactSuggestionStatus,
  RawContactSuggestion,
  SuggestedFields,
} from '@src/service/model/contact/ContactSuggestionModel'
import type { CodableContactTemplateItem } from '@src/service/model/contact/ContactTemplateItemModel'
import type { RawGoogleContactSettings } from '@src/service/model/contact/GoogleContactSettingsModel'
import type { CodableNote } from '@src/service/model/contact/NoteModel'
import type { CodableSharedContactSettings } from '@src/service/model/contact/SharedContactSettingsModel'
import type { BaseEncodableReaction } from '@src/service/model/reactions/BaseReaction'

import type Transport from '.'
import type LegacyPaginated from './lib/LegacyPaginated'
import type { TransactionConfiguration } from './transaction'
import { HttpTransaction } from './transaction'

export type VisibilityType = 'private' | 'org' | 'shared-number' | 'global'

type PaginatedContactSuggestionsResponse = {
  data: RawContactSuggestion[]
  totalItems: number
  nextPageToken?: string
}

export default class ContactsClient {
  constructor(private transport: Transport) {}

  fetch(
    query: {
      since?: Date
      limit?: number
      lastId?: string
      includeDeleted?: boolean
      dir?: 'asc' | 'desc'
    },
    transaction: TransactionConfiguration = {},
  ): Promise<LegacyPaginated<CodableContact>> {
    return this.transport.queue(
      new HttpTransaction({
        url: `${config.CONTACT_SERVICE_URL}contact`,
        query,
        ...transaction,
      }),
    )
  }

  fetchByIds(ids: string[]): Promise<{ contacts: CodableContact[] }> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.CONTACT_SERVICE_URL}contact/bulk-get`,
        body: {
          ids,
        },
      }),
    )
  }

  googleSync(code: string, redirectUri: string): Promise<any> {
    code = encodeURIComponent(code)
    redirectUri = encodeURIComponent(redirectUri)
    return this.transport.queue(
      new HttpTransaction({
        url: `${config.CONTACT_SERVICE_URL}contact/people/sync?code=${code}&redirectUri=${redirectUri}`,
        method: 'post',
      }),
    )
  }

  createBulk(contacts: CodableContact[]) {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.CONTACT_SERVICE_URL}contact`,
        body: contacts,
        queue: 'contact-import',
        retry: true,
      }),
    )
  }

  async update(contact: CodableContact): Promise<any> {
    const cont = whitelist(contact, [
      'id',
      'firstName',
      'lastName',
      'company',
      'role',
      'items',
      'location',
      'pictureUrl',
      'source',
      'sourceName',
      'userId',
      'sharedWith',
    ])
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.CONTACT_SERVICE_URL}contact/${contact.id}`,
        body: cont,
        queue: contact.id,
        retry: true,
      }),
    )
  }

  async updateContactItem(
    contactId: string,
    contactItem: RawContactItem,
  ): Promise<unknown> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.CONTACT_SERVICE_URL}contact/${contactId}/item/${contactItem.id}`,
        body: contactItem,
        queue: contactItem.id,
        retry: true,
      }),
    )
  }

  merge(id: string, withIds: string[]): Promise<{ results: 'ok'; newContactId: string }> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.CONTACT_SERVICE_URL}contact/${id}/merge`,
        body: withIds,
        queue: id,
        retry: true,
      }),
    )
  }

  delete(id: string): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'delete',
        url: `${config.CONTACT_SERVICE_URL}contact/${id}`,
        queue: id,
        retry: true,
      }),
    )
  }

  shareBulk(ids: string[], sharedIds: string[], overrideSharing = true): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.CONTACT_SERVICE_URL}contact/share`,
        body: { contactIds: ids, sharedIds, overrideSharing },
        queue: 'bulk-contact-share',
        retry: true,
      }),
    )
  }

  deleteBulk(ids: string[]): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'delete',
        url: `${config.CONTACT_SERVICE_URL}contact`,
        body: ids,
        queue: 'contact-delete',
        retry: true,
      }),
    )
  }

  note = {
    put: (contactId: string, note: CodableNote): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.CONTACT_SERVICE_URL}contact/${contactId}/note`,
          body: note,
          queue: contactId,
          retry: true,
        }),
      )
    },

    deleteNote: (contactId: string, noteId: string): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.CONTACT_SERVICE_URL}contact/${contactId}/note/${noteId}`,
          queue: contactId,
          retry: true,
        }),
      )
    },

    addReaction: (
      contactId: string,
      noteId: string,
      reaction: BaseEncodableReaction,
    ): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.CONTACT_SERVICE_URL}contact/${contactId}/note/${noteId}/reaction`,
          body: reaction,
          retry: true,
        }),
      )
    },

    deleteReaction: (contactId: string, noteId: string, reactionId: string) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.CONTACT_SERVICE_URL}contact/${contactId}/note/${noteId}/reaction/${reactionId}`,
          retry: true,
        }),
      )
    },
  }

  csv = {
    import: (body: {
      fileName: string
      name: string
      type: string
      mapping: Record<string, CsvImportV2MappingValue>
      meta: object
    }): Promise<CodableCsvImportV2 & { s3SignedUrl: string }> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.CONTACT_SERVICE_URL}import`,
          body,
          queue: 'contact-import',
        }),
      )
    },

    getAllImports: (includeDeleted = true): Promise<CodableCsvImportV2[]> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.CONTACT_SERVICE_URL}import`,
          query: { includeDeleted },
        }),
      )
    },

    importStatus: (id: string, jobMeta: { status: CsvImportStatus; retries: number }) => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.CONTACT_SERVICE_URL}import/${id}`,
          method: 'put',
          body: {
            job: jobMeta,
          },
        }),
      )
    },

    submitFile: (file: File, url: string) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url,
          body: file,
          queue: 'contact-csv-file',
          headers: {
            'Content-Type': 'text/csv',
          },
        }),
      )
    },

    deleteImport: (id: string) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.CONTACT_SERVICE_URL}import/${id}`,
        }),
      )
    },
  }

  template = {
    fetch: (): Promise<CodableContactTemplateItem[]> => {
      return this.transport.queue(
        new HttpTransaction({ url: `${config.CONTACT_SERVICE_URL}contact/template` }),
      )
    },

    put: (item: CodableContactTemplateItem): Promise<CodableContactTemplateItem> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.CONTACT_SERVICE_URL}contact/template/${item.id}`,
          body: item,
          queue: item.id,
          retry: true,
        }),
      )
    },

    post: (
      items: CodableContactTemplateItem[],
    ): Promise<CodableContactTemplateItem[]> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.CONTACT_SERVICE_URL}contact/template`,
          body: items,
          queue: 'contact-template-bulk',
          retry: true,
        }),
      )
    },

    create: (item: CodableContactTemplateItem): Promise<CodableContactTemplateItem> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.CONTACT_SERVICE_URL}contact/template`,
          body: item,
          queue: item.id,
          retry: true,
        }),
      )
    },

    delete: (item: CodableContactTemplateItem): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.CONTACT_SERVICE_URL}contact/template/${item.id}`,
          queue: item.id,
          retry: true,
        }),
      )
    },
  }

  settings = {
    fetchGoogleSettings: (): Promise<RawGoogleContactSettings[]> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.CONTACT_SERVICE_URL}contact/settings`,
        }),
      )
    },

    fetch: (): Promise<CodableSharedContactSettings> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.CONTACT_SERVICE_URL}settings`,
        }),
      )
    },

    put: (
      settings: Partial<
        Pick<CodableSharedContactSettings, 'defaultSharingIds' | 'suggestionsEnabled'>
      >,
    ) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.CONTACT_SERVICE_URL}settings`,
          body: settings,
        }),
      )
    },

    resync: (settings: RawGoogleContactSettings) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.CONTACT_SERVICE_URL}contact/people/sync?source=${settings.source}`,
          retry: false,
        }),
      )
    },

    delete: (id: string) => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.CONTACT_SERVICE_URL}contact/settings/${id}`,
          retry: true,
        }),
      )
    },
  }

  suggestions = {
    for: (orgId: string) => ({
      fetchByPhoneNumbers: ({
        phoneNumbers,
      }: {
        phoneNumbers: string[]
      }): Promise<PaginatedContactSuggestionsResponse> => {
        return this.transport.queue(
          new HttpTransaction({
            url: `${config.CONTACT_SERVICE_URL}suggestions`,
            query: {
              status: 'pending',
              orgId,
              externalNumbers: phoneNumbers,
            },
          }),
        )
      },
    }),

    update: (
      id: string,
      status: Omit<ContactSuggestionStatus, 'pending'>,
      suggestedFields: SuggestedFields,
    ): Promise<RawContactSuggestion> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.CONTACT_SERVICE_URL}suggestions/${id}`,
          body: {
            status,
            suggestedFields,
          },
        }),
      )
    },

    // Marks the number as NOT spam. The route reads "spam" because the BE models
    // "marked safe" as creating a SPAM-type ContactSuggestion. Its presence is the
    // signal that the user cleared the spam classification. So POST suggestions/spam
    // removes spam, it doesn't flag it.
    markNotSpam: (phoneNumber: string): Promise<void> =>
      this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.CONTACT_SERVICE_URL}suggestions/spam`,
          body: { phoneNumber },
        }),
      ),
  }

  integration = {
    get: (identifiers: string[]): Promise<LegacyPaginated<CodableContact>> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.CONTACT_SERVICE_URL}integration`,
          query: { identifiers },
        }),
      )
    },
  }
}
